export interface WechatJsapiPayConfig {
  appId: string;
  timeStamp: string;
  nonceStr: string;
  package: string;
  signType: string;
  paySign: string;
  mock_mode?: boolean;
  channel?: string;
}

declare global {
  interface Window {
    WeixinJSBridge?: {
      invoke: (
        name: string,
        params: Record<string, unknown>,
        callback: (result: { err_msg?: string; errMsg?: string }) => void,
      ) => void;
    };
  }
}

const isString = (value: unknown): value is string => typeof value === "string" && value.length > 0;

const sleep = (durationMs: number) => new Promise((resolve) => window.setTimeout(resolve, durationMs));

export const isWechatJsapiPayConfig = (config: unknown): config is WechatJsapiPayConfig => {
  if (!config || typeof config !== "object") {
    return false;
  }
  const raw = config as Record<string, unknown>;
  return (
    raw.mock_mode === false &&
    raw.channel === "wechat_jsapi" &&
    isString(raw.appId) &&
    isString(raw.timeStamp) &&
    isString(raw.nonceStr) &&
    isString(raw.package) &&
    isString(raw.signType) &&
    isString(raw.paySign)
  );
};

export const isWechatPayAlreadyPaid = (config: unknown): boolean => {
  if (!config || typeof config !== "object") {
    return false;
  }
  const raw = config as Record<string, unknown>;
  return raw.mock_mode === false && raw.channel === "wechat_jsapi" && raw.already_paid === true;
};

export const invokeWechatJsapiPay = (config: WechatJsapiPayConfig) =>
  new Promise<void>((resolve, reject) => {
    const run = () => {
      if (!window.WeixinJSBridge) {
        reject(new Error("当前微信支付环境不可用"));
        return;
      }
      window.WeixinJSBridge.invoke(
        "getBrandWCPayRequest",
        {
          appId: config.appId,
          timeStamp: config.timeStamp,
          nonceStr: config.nonceStr,
          package: config.package,
          signType: config.signType,
          paySign: config.paySign,
        },
        (result) => {
          const message = result.err_msg || "";
          if (message === "get_brand_wcpay_request:ok") {
            resolve();
            return;
          }
          if (message === "get_brand_wcpay_request:cancel") {
            reject(new Error("已取消微信支付"));
            return;
          }
          reject(new Error("微信支付未完成"));
        },
      );
    };

    if (window.WeixinJSBridge) {
      run();
      return;
    }
    document.addEventListener("WeixinJSBridgeReady", run, { once: true });
  });

export const waitForWechatPayResultReady = async (
  fetchResult: () => Promise<{ pay_status?: number | string }>,
  options: { attempts?: number; intervalMs?: number } = {},
) => {
  const attempts = options.attempts ?? 10;
  const intervalMs = options.intervalMs ?? 1000;

  for (let index = 0; index < attempts; index += 1) {
    const result = await fetchResult();
    if (Number(result.pay_status) === 1) {
      return result;
    }
    if (index < attempts - 1) {
      await sleep(intervalMs);
    }
  }

  return fetchResult();
};
